<template>
  <div
    class="set-value-wrapper absolute flex min-w-max flex-col justify-between space-y-6 rounded-sm bg-white p-5 whitespace-nowrap shadow-sm"
    :class="isCheckboxDropdownShown ? 'min-h-[380px]' : 'min-h-[240px]'"
  >
    <do-month-picker
      v-if="requiresMonthsLookbackPicker"
      :value="lookbackTime"
      month-picker-menu-class="max-h-70"
      @context:change="handleContextChange"
      @context:update-all="handleAllContextChange"
    />
    <div class="flex flex-col space-y-6">
      <lf-dropdown
        v-if="requiresYearSelection"
        data-cy="select-year"
        class="h-10 w-full"
        name="selectedYear"
        :options="yearOptions"
        :value="selectedYear"
        :placeholder="$t('COMMON.YEAR')"
        full-width
        @change="handleYearChange"
      />
      <lf-dropdown
        v-if="showScoreModelDropdown"
        class="h-10 w-full"
        name="consumerScoreModel"
        :options="attributeOptions"
        :value="consumerScoreModel"
        :placeholder="$t('SCORECARDS.SCORE_MODEL')"
        full-width
        hide-placeholder-when-value
        @change="handleScoreModelChange"
      />
      <lf-dropdown
        class="h-10 w-full"
        name="product_types"
        :placeholder="$t('COMMON.TYPE')"
        :options="types"
        :disabled="hasOneOrLessOptions"
        :value="type"
        full-width
        @change="handleTypeChange"
      />
      <template v-if="hasValueOptions">
        <lf-checkbox-dropdown
          v-if="isCheckboxDropdown"
          v-model="value"
          class="conditional-set-value-case h-10 max-w-[280px]"
          :placeholder="$t('COMMON.VALUE')"
          :options="cleanedOptions"
          :option-formatter="(value: string) => truncateWithEllipsis(value, 28)"
          name="value_checkbox_dropdown"
          enable-search
          width-full
          dropdown-width-full
          options-list-height-small
          @change-list-visibility="isCheckboxDropdownShown = $event"
        />
        <lf-dropdown
          v-else
          class="h-10 w-full"
          name="value_dropdown"
          :placeholder="$t('COMMON.VALUE')"
          :options="cleanedOptions"
          :value="value"
          full-width
          @change="handleValueChange"
        />
      </template>
      <lf-dropdown
        v-else-if="type === 'boolean'"
        class="h-10 w-full"
        name="value_dropdown"
        :placeholder="$t('COMMON.VALUE')"
        :options="BOOLEAN_OPTIONS"
        :value="value"
        full-width
        @change="handleValueChange"
      />
      <div v-else-if="isDateField && !isNullRelatedType">
        <lf-date-picker
          v-model="dateValue"
          :formatter="yearMonthDayDateFormatter"
          :as-single="!isRange"
          :value="dateValue"
          :class="{ 'lf-date-picker': isRange && useRangeFormating }"
        />
      </div>
      <div v-else-if="attributeSupportsSyncContext && isAttributeSentilink">
        <lf-dropdown
          class="h-10 w-full"
          name="confidence_level"
          :placeholder="$t('ORCHESTRATION.CONFIDENCE_VALUE')"
          :options="sentilinkConfidenceLevels"
          :value="value"
          full-width
          @change="handleValueChange"
        />
      </div>
      <div v-else-if="!isNullRelatedType" class="flex space-x-4">
        <lf-input
          v-tooltip="{
            content: t('SCORECARDS.BUSINESS_ZIP_CODE_HINT'),
            onShow: () => showInputForArrayValues,
          }"
          name="value_input"
          data-cy="value-input"
          class="h-10 w-full"
          :placeholder="valuePlaceholder"
          :value="value"
          :has-error="!!attributeErrors[data.id]?.value"
          :custom-error="attributeErrors[data.id]?.value"
          no-margin
          @key-released="handleValueChange"
        />
        <lf-input
          v-if="isRange || isNaicsCodeRange"
          name="value_input_2"
          data-cy="value-input-2"
          class="h-10 w-full"
          :placeholder="isRange ? $t('COMMON.MAX_VALUE') : $t('COMMON.TO')"
          :value="secondValue"
          :has-error="!!attributeErrors[data.id]?.secondValue"
          :custom-error="attributeErrors[data.id]?.secondValue"
          no-margin
          @key-released="handleSecondValueChange"
        />
        <lf-dropdown
          v-if="isTimeInBusiness"
          class="mr-4 max-h-10 min-w-50"
          :options="timeInBusinessOptions"
          :value="tibMeasure"
          full-width
          @change="handleTIBMeasureChange"
        />
      </div>
      <lf-checkbox-dropdown
        v-if="isNaicsCodeRange"
        v-model="naicsRangeDigitsValue"
        class="h-10"
        :placeholder="$t('COMMON.DIGITS')"
        :max-items-displayed="7"
        :options="NAICS_RANGE_DIGITS"
        name="naics_digits_dropdown"
        width-full
        dropdown-width-full
        options-list-height-small
        @change-list-visibility="isNaicsDigitsShown = $event"
      />
      <div class="flex flex-col space-y-6">
        <lf-checkbox
          v-if="attributeSupportsSyncOrFillContext"
          v-model="syncAttributes"
          :value="syncOrFillContextAttributeValue"
          :disabled="isSyncAttributesCheckboxDisabled"
          name="management-selection-lendflow-sync"
          class="mr-auto"
          :class="{
            'order-1 mt-6': attributeSupportsPrimaryContext,
          }"
        >
          {{ attributeSyncLabel }}
        </lf-checkbox>
        <lf-checkbox
          v-if="
            attributeSupportsSyncContext ||
            attributeSupportsSyncOrFillContext ||
            attributeSupportsPrimaryContext
          "
          v-model="fillAttributes"
          :value="syncOrFillContextAttributeValue"
          :disabled="isFillAttributesCheckboxDisabled"
          name="management-selection-lendflow-fill"
          class="mr-auto"
          :class="{
            'order-0 -mt-2':
              attributeSupportsPrimaryContext ||
              (attributeSupportsSyncContext && !isAttributeSentilink),
          }"
        >
          {{ attributeFillLabel }}
        </lf-checkbox>
        <lf-checkbox
          v-if="attributeSupportsPrimaryContext"
          v-model="primaryAttributes"
          :value="primaryContextAttributeValue"
          :disabled="!syncAttributes.length"
          name="management-selection-lendflow-set_primary"
          class="order-2 mr-auto"
        >
          {{ setAsPrimaryLabel }}
        </lf-checkbox>
      </div>
    </div>
    <primary-button
      :disabled="primaryButtonDisabled"
      data-cy="apply-conditional-set-value-button"
      @click="handleApply"
    >
      {{ $t("COMMON.APPLY") }}
    </primary-button>
  </div>
</template>

<script lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

import has from "lodash/has";

import { getObjectKeys, looksLikeNumber } from "@/helpers/common";
import { BPR_CONSUMER_SCORE_MODELS } from "@/helpers/constants/equifax";
import { checkValueType, isArrayInputAttribute } from "@/helpers/scorecards";
import { useNotification } from "@/hooks/notifications";

import type { BOP_CONSUMER_SCORE_MODELS } from "@/helpers/constants/experian";
import type { ConditionType } from "@/models/common";
import type {
  IAttribute,
  IBopConsumerScoreModelLevel,
  IConditionalSetValueModel,
  IConditionalSetWithContext,
  IContextSetValueModel,
  IRelativeDateRange,
  ISpecificDateRange,
  ISyncAttributes,
  LookbackValue,
  NaicsRange,
  OverwriteAddressOrAddSecondaryContexts,
  PrimaryAttributes,
  RelativeLookbackRange,
  SpecificYear,
  SyncAttributes,
  SyncOrFillContexts,
  SyncOrFillEligibleAttributes,
  TimeMeasure,
} from "@/models/orchestration";
import type { PropType } from "vue";
</script>

<script setup lang="ts">
import { useI18n } from "vue-i18n";

import {
  DEFAULT_LOOKBACK_PERIOD,
  NAICS_RANGE_DIGITS,
  PROVIDER_SENTILINK,
  TIME_CONVERSION_SCALES,
  TIME_IN_BUSINESS_SERVICE_EXCEPTIONS,
  TYPE_IN,
  TYPE_IN_CASE_INSENSITIVE,
  TYPE_IN_FLEXIBLE,
  TYPE_NOT_IN,
  TYPE_NOT_IN_CASE_INSENSITIVE,
  TYPE_NOT_IN_FLEXIBLE,
  TYPE_NOT_NULL,
  TYPE_NULL,
  TYPE_RANGE,
} from "@/helpers/constants";
import {
  HYPHENATED_DATE_FORMAT,
  yearMonthDayDateFormatter,
} from "@/helpers/constants/dateTime";
import {
  formatDateCustom,
  transformStringToArray,
  truncateWithEllipsis,
} from "@/helpers/formatting";
import {
  getHasOneOrLessOptions,
  getIsTimeInBusiness,
  getPrimaryContextAttribute,
  getRuleTypes,
  isNullishAttributeWithContext,
  supportsPrimaryContext,
  supportsSyncContext,
  supportsSyncOrFillContext,
  syncContextAttribute,
  syncOrFillContextAttribute,
  typeIsNaicsCodeRange,
} from "@/helpers/orchestration";
import { filterInvalidValueOptions } from "@/helpers/orchestration";
import {
  useSentilinkConfidenceLevels,
  useTimeInBusinessOptions,
  useYearsOptions,
} from "@/hooks/orchestration";

import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";

const emit = defineEmits(["save", "context:update-all"]);

const props = defineProps({
  data: {
    type: Object as PropType<IAttribute>,
    required: true,
  },
  context: {
    type: Object as PropType<IContextSetValueModel>,
  },
  conditionalModel: {
    type: Object as PropType<IConditionalSetValueModel>,
    default: () => ({
      type: "",
      value: "",
    }),
  },
  selectedValues: {
    type: Object as PropType<{
      type: ConditionType;
      value: string | undefined;
      secondValue: string | undefined;
    }>,
    default: () => ({}),
  },
});

const { t } = useI18n();

const BOOLEAN_OPTIONS = {
  true: "True",
  false: "False",
} as const;

const { getters } = useStore();
const { showMessage } = useNotification();

const timeInBusinessOptions = useTimeInBusinessOptions();

const tibMeasure = ref<TimeMeasure>(timeInBusinessOptions.days as TimeMeasure);
const isCheckboxDropdownShown = ref(false);
const isNaicsDigitsShown = ref(false);
const fillAttributes = ref<SyncAttributes[]>([]);
const syncAttributes = ref<SyncAttributes[]>([]);
const primaryAttributes = ref<boolean>(false);
const hasMonthToDate = ref(false);
const isRelativeLookbackRange = ref(false);
const attributeErrors = ref<
  Record<string, { value: string; secondValue: string }>
>({ [props.data.id]: { value: "", secondValue: "" } });

const lookbackTime = ref<string[] | number[]>([]);

const consumerScoreModel = ref<keyof typeof BOP_CONSUMER_SCORE_MODELS | "">("");
const selectedYear = ref<number | string | null>(null);
const types = computed<Record<ConditionType, string>>(() =>
  getRuleTypes(props.data.rules, props.data.id)
);

const useRangeFormating = computed(
  () => props.data.name === t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.START_DATE")
);

const isNullAttrRequiresContext = computed(() =>
  isNullishAttributeWithContext(props.data.id)
);

const isSyncAttributesCheckboxDisabled = computed(() => {
  if (isNullAttrRequiresContext.value) {
    return !!fillAttributes.value.length && !syncAttributes.value.length;
  }
  return attributeSupportsPrimaryContext.value && !!fillAttributes.value.length;
});

const isFillAttributesCheckboxDisabled = computed(() => {
  if (isNullAttrRequiresContext.value) {
    return !!syncAttributes.value.length && !fillAttributes.value.length;
  }
  return attributeSupportsPrimaryContext.value && !!syncAttributes.value.length;
});

const requiresMonthsLookbackPicker = computed(
  () =>
    props.data.options?.month_picker_range ||
    props.data.options?.sync_or_fill_attributes_with_date_range
);

const requiresYearSelection = computed(() => !!props.data.meta?.specific_year);

const isTransuionServiceAttribute = computed(
  () => props.data.service_id?.startsWith("transunion") ?? false
);

const showInputForArrayValues = computed(() =>
  isArrayInputAttribute(props.data.service_id, props.data.id)
);

const showScoreModelDropdown = computed(
  () =>
    props.data?.options &&
    has(props.data.options, "consumer_score_models_levels")
);

const setAsPrimaryLabel = computed(
  () =>
    `${t("ORCHESTRATION.SYNC.SET_AS_PRIMARY")} ${t(
      `COMMON.${getPrimaryContextAttribute(
        props.data.id as PrimaryAttributes
      )?.toUpperCase()}`
    )}`
);

const cleanedOptions = computed(() =>
  filterInvalidValueOptions(props.data?.options || {})
);

const hasValueOptions = computed(
  () =>
    !isNullRelatedType.value &&
    props.data.options &&
    !!Object.keys(cleanedOptions.value).length &&
    !requiresMonthsLookbackPicker.value &&
    !isTimeInBusiness.value &&
    !isDateField.value &&
    !isNaicsCodeRange.value &&
    TIME_IN_BUSINESS_SERVICE_EXCEPTIONS.indexOf(
      props.data.id as (typeof TIME_IN_BUSINESS_SERVICE_EXCEPTIONS)[number]
    ) === -1
);

const bopConsumerScoreModels = computed<typeof BOP_CONSUMER_SCORE_MODELS>(
  () => getters["options/experianBopConsumerScoreModels"]
);

const hasValue = computed(() => {
  if (isDateField.value) {
    if (isRange.value) {
      return !!dateValue.value[0] && !!dateValue.value[1];
    }

    return !!dateValue.value[0];
  }

  return Array.isArray(value.value) ? !!value.value.length : value.value !== "";
});

const primaryButtonDisabled = computed(() => {
  if (isNullRelatedType.value) {
    return false;
  }

  if (
    !!attributeErrors.value[props.data.id].value ||
    !!attributeErrors.value[props.data.id].secondValue
  ) {
    return true;
  }

  const maxLessThanMin =
    isRange.value &&
    !isDateField.value &&
    (!secondValue.value || Number(secondValue.value) <= Number(value.value));

  const disabled = !type.value || !hasValue.value || maxLessThanMin;

  if (!showScoreModelDropdown.value) {
    return disabled;
  }

  return !consumerScoreModel.value || disabled;
});

const hasOneOrLessOptions = computed(() => getHasOneOrLessOptions(types.value));

const getInitialType = () => {
  if (props.selectedValues.type) {
    return props.selectedValues.type;
  }

  if (hasOneOrLessOptions.value) {
    return getObjectKeys(types.value)[0];
  }

  return (props.conditionalModel?.type || "") as ConditionType;
};

const type = ref<ConditionType>(getInitialType());

const { sentilinkConfidenceLevels } = useSentilinkConfidenceLevels(type);

const isCheckboxDropdown = computed(() =>
  checkValueType(props.data.rules, "array")
);

const isRange = computed(() => type.value === TYPE_RANGE);
const isNaicsCodeRange = computed(() => typeIsNaicsCodeRange(type.value));
const isDateField = computed(() => checkValueType(props.data.rules, "date"));
const isNullRelatedType = computed(
  () => type.value === TYPE_NULL || type.value === TYPE_NOT_NULL
);
const valuePlaceholder = computed(() => {
  if (isRange.value) {
    return t("COMMON.MIN_VALUE");
  }
  if (isNaicsCodeRange.value) {
    return t("COMMON.FROM");
  }
  return t("COMMON.VALUE");
});
const value = ref(
  isNaicsCodeRange.value
    ? (props.conditionalModel?.value as NaicsRange)?.from
    : props.conditionalModel?.value
);
const secondValue = ref(
  isNaicsCodeRange.value
    ? (props.conditionalModel?.value as NaicsRange)?.through
    : props.conditionalModel?.secondValue
);
const naicsRangeDigitsValue = ref<number[] | string[]>(
  (props.conditionalModel?.value as NaicsRange)?.digits
    ? (props.conditionalModel?.value as NaicsRange)?.digits?.map(String)
    : []
);
const dateValue = ref<string[]>(setInitialDateValue());

const handleTypeChange = (val: ConditionType) => {
  type.value = val;
  if (isNaicsCodeRange.value || isRange.value) {
    value.value = "";
    secondValue.value = "";
    return;
  }
  if (
    [
      TYPE_IN,
      TYPE_NOT_IN,
      TYPE_IN_FLEXIBLE,
      TYPE_NOT_IN_FLEXIBLE,
      TYPE_IN_CASE_INSENSITIVE,
      TYPE_NOT_IN_CASE_INSENSITIVE,
    ].includes(val)
  ) {
    value.value = [];
    return;
  }
  value.value = "";
};

const isTimeInBusiness = computed(() => getIsTimeInBusiness(props.data.id));

const attributeSupportsSyncContext = computed(
  () => !isNullRelatedType.value && supportsSyncContext(props.data.id)
);

const attributeSupportsSyncOrFillContext = computed(
  () =>
    (!isNullRelatedType.value ||
      isNullishAttributeWithContext(props.data.id)) &&
    supportsSyncOrFillContext(props.data.id as SyncOrFillEligibleAttributes)
);

const attributeSupportsPrimaryContext = computed(
  () =>
    !isNullRelatedType.value &&
    supportsPrimaryContext(props.data.id as PrimaryAttributes)
);

const attributeFillLabel = computed(() => {
  if (attributeSupportsPrimaryContext.value) {
    return `${t("ORCHESTRATION.SYNC.OVERWRITE")} ${t(
      `COMMON.${getPrimaryContextAttribute(
        props.data.id as PrimaryAttributes
      )?.toUpperCase()}`
    )}`;
  }

  if (syncOrFillContextAttributeValue.value === "business_name") {
    return t(
      `ORCHESTRATION.SYNC.${syncOrFillContextAttributeValue.value?.toUpperCase()}`,
      {
        action: t("ORCHESTRATION.SYNC.UPDATE"),
        addendum: t("ORCHESTRATION.SYNC.BASED_ON_SCORE"),
      }
    );
  }

  return t(
    `ORCHESTRATION.SYNC.${syncOrFillContextAttributeValue.value?.toUpperCase()}`,
    {
      action: t("ORCHESTRATION.SYNC.UPDATE"),
      addendum: attributeSupportsSyncContext.value
        ? t("ORCHESTRATION.SYNC.BASED_ON_CONFIDENCE")
        : "",
    }
  );
});

const attributeSyncLabel = computed(() => {
  if (attributeSupportsPrimaryContext.value) {
    return `${t("ORCHESTRATION.SYNC.ADD_SECONDARY")} ${t(
      `COMMON.${getPrimaryContextAttribute(
        props.data.id as PrimaryAttributes
      )?.toUpperCase()}`
    )}`;
  }
  return t(
    `ORCHESTRATION.SYNC.${syncOrFillContextAttributeValue.value?.toUpperCase()}`,
    {
      action: t("ORCHESTRATION.SYNC.OVERWRITE"),
    }
  );
});

const syncOrFillContextAttributeValue = computed(() =>
  attributeSupportsSyncOrFillContext.value
    ? syncOrFillContextAttribute(props.data.id as SyncOrFillEligibleAttributes)
    : syncContextAttribute(props.data.id)
);

const isAttributeSentilink = computed(() =>
  syncOrFillContextAttributeValue.value?.includes(PROVIDER_SENTILINK)
);

const primaryContextAttributeValue = computed(() => {
  if (!syncAttributes.value.length) {
    return null;
  }
  return getPrimaryContextAttribute(props.data.id as PrimaryAttributes);
});

const attributeOptions = computed(() => {
  if (props.data.service_id?.startsWith("equifax")) {
    return BPR_CONSUMER_SCORE_MODELS;
  }

  return bopConsumerScoreModels.value;
});

const transunionAttributeModel = computed(() => {
  if (!isTransuionServiceAttribute.value) {
    return "";
  }

  return props.data.active_model;
});

const yearOptions = useYearsOptions;

const validateAttribute = (attribute: string, val: string | string[]) => {
  const attributeErrorConditions: Record<
    string,
    { invalid: boolean; message: string }
  > = {
    middesk_business_search_name_search: {
      invalid:
        !looksLikeNumber(val) ||
        (looksLikeNumber(val) && (Number(val) < 0 || Number(val) > 1)),
      message: t("ERRORS.NUMBER_RANGE_ERROR", {
        min: 0,
        max: 1,
      }),
    },
  };

  const rules = props.data.rules.value.split("|");

  if (rules.includes("numeric") && isNaN(Number(val))) {
    attributeErrorConditions[attribute] = {
      invalid: true,
      message: t("ERRORS.NUMERIC_ERROR"),
    };
  }

  return attributeErrorConditions[attribute];
};

const handleValueChange = (val: string | string[]) => {
  value.value = val;
  const attributeValidationState = validateAttribute(props.data.id, val);

  if (attributeValidationState?.invalid && val !== "") {
    attributeErrors.value[props.data.id].value =
      attributeValidationState.message;
    return;
  }

  attributeErrors.value[props.data.id].value = "";
};

const handleTIBMeasureChange = (val: TimeMeasure) => {
  tibMeasure.value = val;
};

const handleSecondValueChange = (val: string | string[]) => {
  secondValue.value = val;
  const attributeValidationState = validateAttribute(props.data.id, val);

  if (attributeValidationState?.invalid && val !== "") {
    attributeErrors.value[props.data.id].secondValue =
      attributeValidationState.message;
    return;
  }

  attributeErrors.value[props.data.id].secondValue = "";
};

const handleContextChange = (data: LookbackValue) => {
  lookbackTime.value = data.lookback;
  hasMonthToDate.value = data.hasMonthToDate;
  isRelativeLookbackRange.value = data.type === "relative_lookback_range";
};

const handleAllContextChange = (data: LookbackValue) => {
  lookbackTime.value = data.lookback;
  hasMonthToDate.value = data.hasMonthToDate;

  let context = null;

  if (lookbackTime.value.length === 2) {
    context = {
      type: "specific_date_range",
      value: {
        from: formatDateCustom(lookbackTime.value[0], "yyyy-MM-dd"),
        through: formatDateCustom(lookbackTime.value[1], "yyyy-MM-dd"),
      },
    };
  }

  if (lookbackTime.value.length === 1) {
    context = {
      type: "relative_date_range",
      value: {
        lookback_months: lookbackTime.value[0],
        include_month_to_date: hasMonthToDate.value,
      },
    };
  }

  if (isRelativeLookbackRange.value) {
    if (lookbackTime.value.length === 1) {
      context = {
        type: "relative_lookback_range",
        value: {
          lookback_months_from: lookbackTime.value[0],
          lookback_months_through: lookbackTime.value[0],
        },
      };
    } else if (lookbackTime.value.length === 2) {
      context = {
        type: "relative_lookback_range",
        value: {
          lookback_months_from: lookbackTime.value[0],
          lookback_months_through: lookbackTime.value[1],
        },
      };
    }
  }
  emit("context:update-all", context);
};

const getDaysValue = (val: number) =>
  String(val * (TIME_CONVERSION_SCALES[tibMeasure.value] ?? 1));

const getConvertedVal = (
  val: string | number | boolean | string[] | null | undefined | NaicsRange
) => (isTimeInBusiness.value ? getDaysValue(Number(val)) : val);

const getSyncOrFillWithDateRangeContext = () => {
  const timeContext = getTimeContext();
  const syncOrFillContext = getSyncOrFillContext();
  const context = {
    type: "sync_or_fill_attributes_with_date_range",
    value: {
      ...(timeContext.value || {}),
      ...syncOrFillContext.value,
    },
  } as IContextSetValueModel;

  return context;
};

const getTimeContext = () => {
  let context: IContextSetValueModel = {
    type: "relative_lookback_range",
  };

  if (isRelativeLookbackRange.value) {
    context = {
      type: "relative_lookback_range",
      value: {
        lookback_months_from: Number(lookbackTime.value[0]),
        lookback_months_through: Number(lookbackTime.value[1]),
      },
    };
  } else if (lookbackTime.value.length === 2) {
    context = {
      type: "specific_date_range",
      value: {
        from: formatDateCustom(lookbackTime.value[0] as string, "yyyy-MM-dd"),
        through: formatDateCustom(
          lookbackTime.value[1] as string,
          "yyyy-MM-dd"
        ),
      },
    };
  } else if (lookbackTime.value.length === 1) {
    context = {
      type: "relative_date_range",
      value: {
        lookback_months: Number(lookbackTime.value[0]),
        include_month_to_date: hasMonthToDate.value,
      },
    };
  }
  return context;
};

const getSyncOrFillContext = () => {
  let context: IContextSetValueModel = {
    type: "sync_or_fill_attributes",
    value: {
      fill: fillAttributes.value,
      sync: syncAttributes.value,
    },
  };

  if (supportsPrimaryContext(props.data.id as PrimaryAttributes)) {
    context = {
      type: "overwrite_or_add_secondary_attributes",
      value: {
        overwrite: fillAttributes.value,
        add: syncAttributes.value,
        primary: primaryAttributes.value,
      },
    };
  }

  return context;
};

const handleApply = () => {
  const value1 = getConvertedVal(value.value);

  const value2 = getConvertedVal(secondValue.value ?? "");

  let payload: IConditionalSetWithContext = {
    conditional: {
      type: type.value,
      value:
        isDateField.value && !isNullRelatedType.value
          ? dateValue.value[0]
          : value1,
    },
  };

  if (
    isNullRelatedType.value &&
    !isNullishAttributeWithContext(props.data.id) &&
    !showScoreModelDropdown.value &&
    !isTransuionServiceAttribute.value &&
    !requiresMonthsLookbackPicker.value
  ) {
    payload.conditional.value = null;
    emit("save", payload);
    return;
  }

  if (isNullRelatedType.value) {
    payload.conditional.value = null;
  }

  if (isRange.value) {
    payload = {
      conditional: {
        ...payload.conditional,
        secondValue: isDateField.value
          ? (dateValue.value[1] as string)
          : (value2 as string) || undefined,
      },
    };
  }

  if (isNaicsCodeRange.value) {
    if (!value1 || !value2) {
      showMessage(t("COMMON.ERROR_RANGE_REQUIRED"), "error");
      return;
    }
    payload = {
      conditional: {
        type: type.value,
        value: {
          from: Number(value1),
          through: Number(value2),
          digits: naicsRangeDigitsValue.value.map(Number),
        },
      },
    };
  }

  if (showInputForArrayValues.value) {
    payload.conditional.value = transformStringToArray(value.value?.toString());
    emit("save", payload);
    return;
  }

  if (props.data.options?.sync_or_fill_attributes_with_date_range) {
    payload = {
      ...payload,
      context: getSyncOrFillWithDateRangeContext(),
    };
    emit("save", payload);
    return;
  }

  if (supportsSyncContext(props.data.id)) {
    payload = {
      ...payload,
      context: {
        type: "sync_attributes",
        value: {
          attributes: fillAttributes.value,
        },
      },
    };
  } else if (
    supportsSyncOrFillContext(props.data.id as SyncOrFillEligibleAttributes)
  ) {
    payload = {
      ...payload,
      context: getSyncOrFillContext(),
    };
  } else if (requiresMonthsLookbackPicker.value) {
    payload = {
      ...payload,
      context: getTimeContext(),
    };
  } else if (requiresYearSelection.value) {
    if (!selectedYear.value) {
      showMessage(t("COMMON.ERROR_YEAR_REQUIRED"), "error");
      return;
    }
    payload = {
      ...payload,
      context: {
        type: "specific_year",
        value: { year: selectedYear.value },
      },
    };
  } else if (showScoreModelDropdown.value && consumerScoreModel.value) {
    payload = {
      ...payload,
      conditional: {
        ...payload.conditional,
        value: isNullRelatedType.value ? null : payload.conditional.value,
      },
      context: {
        type: "consumer_score_models_levels",
        value: { level: consumerScoreModel.value },
      },
    };
  } else if (isTransuionServiceAttribute.value) {
    const attributesWithNoModel = [
      "transunion_credit_report_credit_freeze_status",
    ];
    if (
      !transunionAttributeModel.value &&
      !attributesWithNoModel.includes(props.data.id)
    ) {
      showMessage(t("COMMON.ERROR_MODEL_REQUIRED"), "error");
      return;
    }

    payload = {
      ...payload,
      conditional: {
        ...payload.conditional,
        value: isNullRelatedType.value ? null : payload.conditional.value,
      },
      context: {
        type: "addon_products",
        value: {
          models: transunionAttributeModel.value
            ? [transunionAttributeModel.value]
            : [],
        },
      },
    };
  }
  emit("save", payload);
};

const handleScoreModelChange = (
  value: keyof typeof BOP_CONSUMER_SCORE_MODELS
) => {
  consumerScoreModel.value = value;
};

const handleYearChange = (value: number | string) => {
  selectedYear.value = value;
};

onMounted(() => {
  if (props.context?.type === "sync_attributes") {
    fillAttributes.value = (props.context.value as ISyncAttributes).attributes;
  } else if (props.context?.type === "sync_or_fill_attributes") {
    fillAttributes.value = (props.context.value as SyncOrFillContexts).fill;
    syncAttributes.value = (props.context.value as SyncOrFillContexts).sync;
  } else if (props.context?.type === "overwrite_or_add_secondary_attributes") {
    fillAttributes.value = (
      props.context.value as OverwriteAddressOrAddSecondaryContexts
    ).overwrite;
    syncAttributes.value = (
      props.context.value as OverwriteAddressOrAddSecondaryContexts
    ).add;
    primaryAttributes.value = (
      props.context.value as OverwriteAddressOrAddSecondaryContexts
    ).primary;
  } else if (props.context?.type === "relative_date_range") {
    lookbackTime.value = [
      (props.context.value as IRelativeDateRange).lookback_months,
    ];
  } else if (props.context?.type === "specific_date_range") {
    lookbackTime.value = [
      (props.context.value as ISpecificDateRange).from,
      (props.context.value as ISpecificDateRange).through,
    ];
  } else if (props.context?.type === "relative_lookback_range") {
    lookbackTime.value = [
      (props.context.value as RelativeLookbackRange).lookback_months_from,
      (props.context.value as RelativeLookbackRange).lookback_months_through,
    ];
    isRelativeLookbackRange.value = true;
  } else if (props.context?.type === "consumer_score_models_levels") {
    consumerScoreModel.value = (
      props.context.value as IBopConsumerScoreModelLevel
    ).level;
  } else if (
    props.context?.type === "sync_or_fill_attributes_with_date_range"
  ) {
    fillAttributes.value =
      (props.context.value as SyncOrFillContexts).fill ?? [];
    syncAttributes.value =
      (props.context.value as SyncOrFillContexts).sync ?? [];
    lookbackTime.value = [
      (props.context.value as RelativeLookbackRange).lookback_months_from ??
        (props.context.value as ISpecificDateRange).from,
      (props.context.value as RelativeLookbackRange).lookback_months_through ??
        (props.context.value as ISpecificDateRange).through,
    ];
    isRelativeLookbackRange.value = typeof lookbackTime.value[0] === "number";
  } else if (props.context?.type === "specific_year") {
    selectedYear.value = (props.context.value as SpecificYear).year;
  }

  if (requiresMonthsLookbackPicker.value && !lookbackTime.value.length) {
    //set default lookback to 6 months
    lookbackTime.value = DEFAULT_LOOKBACK_PERIOD;
    handleContextChange({
      lookback: lookbackTime.value,
      type: "relative_lookback_range",
      hasMonthToDate: false,
    });
  }
});

function setInitialDateValue() {
  if (!isDateField.value || isNullRelatedType.value) {
    return [""];
  }

  if (props.selectedValues.value) {
    if (props.selectedValues.secondValue) {
      return [
        formatDateCustom(props.selectedValues.value, HYPHENATED_DATE_FORMAT),
        formatDateCustom(
          props.selectedValues.secondValue,
          HYPHENATED_DATE_FORMAT
        ),
      ];
    }
    return [
      formatDateCustom(props.selectedValues.value, HYPHENATED_DATE_FORMAT),
    ];
  }

  if (useRangeFormating.value) {
    return value.value && secondValue.value
      ? [value.value as string, secondValue.value as string]
      : [];
  }

  if (isRange.value) {
    return [
      formatDateCustom(value.value as string, "MM/dd/yyyy"),
      formatDateCustom(secondValue.value as string, "MM/dd/yyyy"),
    ];
  }

  return [formatDateCustom(value.value as string, "MM/dd/yyyy")];
}

watch(syncAttributes, (newSyncAttributes) => {
  if (!newSyncAttributes.length) {
    primaryAttributes.value = false;
  }
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.set-value-wrapper {
  width: 320px;
}
@media (max-width: 1440px) {
  :deep(.lf-date-picker .litepie-datepicker) {
    width: 550px;
    max-height: 50vh;
    overflow: auto;
  }
  :deep(.lf-date-picker .litepie-datepicker div:nth-child(2)) {
    flex-wrap: wrap;
  }

  :deep(.lf-date-picker #litepie button span) {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
}
</style>
